/**
 * SideNavLink.tsx — lenke i side-nav
 *
 * Ren React-komponent som erstatter cds-side-nav-link.
 * Bruker next/link for ruting. Active-state markerer gjeldende rute.
 *
 * Props:
 * - href: rute-lenke
 * - active: om lenken er aktiv
 * - icon: ikon foran teksten (øverste nivå)
 * - children: lenketekst
 */

import Link from 'next/link';
import { useContext } from 'react';
import type { NavIconName } from '@/lib/navigation';
import { NAV_ICONS } from './nav-icons';
import { SideNavContext } from './SideNavContext';
import styles from './side-nav-link.module.scss';

interface SideNavLinkProps {
  href: string;
  active?: boolean;
  icon?: NavIconName;
  children: React.ReactNode;
}

export default function SideNavLink({ href, active = false, icon, children }: SideNavLinkProps) {
  const Icon = icon ? NAV_ICONS[icon] : null;
  const { expanded } = useContext(SideNavContext);
  return (
    <li className={styles.item}>
      <Link
        href={href}
        className={active ? styles.linkActive : styles.link}
        aria-current={active ? 'page' : undefined}
        // Rail: teksten er skjult, så navnet vises som tooltip
        title={!expanded && typeof children === 'string' ? children : undefined}
      >
        {Icon && <Icon size={20} className={styles.icon} aria-hidden="true" />}
        {/* side-nav-text: global klasse — skjules i kollapset rail-modus
            (se side-nav.module.scss, lg+ media query) */}
        <span className="side-nav-text">{children}</span>
      </Link>
    </li>
  );
}
